import { useEffect, useMemo, useState } from 'react'

interface DotCardProps {
  target?: number
  duration?: number
  label?: string
  formatValue?: (value: number) => string
  animate?: boolean
}

export default function DotCard({
  target = 777000,
  duration = 2000,
  label = 'Views',
  formatValue,
  animate = false,
}: DotCardProps) {
  const [count, setCount] = useState(0)

  useEffect(() => {
    let start = 0
    const end = target
    const range = end - start
    if (range <= 0) return

    const increment = Math.ceil(end / (duration / 50))
    const timer = setInterval(() => {
      start += increment
      if (start >= end) {
        start = end
        clearInterval(timer)
      }
      setCount(start)
    }, 50)

    return () => clearInterval(timer)
  }, [target, duration])

  const display = useMemo(() => {
    if (formatValue) return formatValue(count)
    if (count < 1000) return `${count}`
    return `${Math.floor(count / 1000)}k`
  }, [count, formatValue])

  return (
    <div className="outer relative h-40 overflow-hidden rounded-xl border border-gray-200 dark:border-zinc-800/50 bg-white dark:bg-zinc-900 p-5 shadow-lg shadow-black/10 dark:shadow-black/30">
      <div
        className={`dot absolute h-3.5 w-3.5 rounded-full bg-violet-400/90 shadow-[0_0_15px_rgba(167,139,250,0.7)] ${
          animate ? 'animate-[circleDot_1400ms_ease-in-out_1_forwards]' : ''
        }`}
      />
      <div className="card relative flex h-full flex-col items-center justify-center text-center">
        <div className="ray absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(139,92,246,0.15),transparent_55%)]" />
        <div className="text relative text-3xl font-bold leading-none text-gray-900 dark:text-slate-100">
          {display}
        </div>
        <div className="label relative mt-2 text-xs uppercase tracking-[0.2em] text-gray-500 dark:text-zinc-500">
          {label}
        </div>
        <div className="line topl pointer-events-none absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-400/40 to-transparent" />
        <div className="line leftl pointer-events-none absolute bottom-0 left-0 top-0 w-px bg-gradient-to-b from-transparent via-violet-400/30 to-transparent" />
        <div className="line bottoml pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-400/40 to-transparent" />
        <div className="line rightl pointer-events-none absolute bottom-0 right-0 top-0 w-px bg-gradient-to-b from-transparent via-violet-400/30 to-transparent" />
      </div>
    </div>
  )
}
